import { Chip, Button } from '@heroui/react';
import { FaLayerGroup, FaCoins, FaTriangleExclamation, FaRotate, FaPenToSquare, FaCheck, FaArrowUpRightFromSquare, FaLink } from 'react-icons/fa6';

// A dub that failed because the destination YouTube channel lost authorization
// can't be fixed by retrying — the channel must be reconnected first. Matches
// ONLY the backend's channel-auth messages ("Destination channel needs to be
// reconnected" / "Destination channel was disconnected"); anything else (e.g.
// yt-dlp's "RemoteDisconnected" transport errors) is a normal retryable failure.
const isReauthFailure = (error) => !!error && /channel (?:needs to be reconnected|was disconnected)/i.test(error);
import Thumb from './primitives/Thumb.jsx';
import ChannelAvatar from './primitives/ChannelAvatar.jsx';
import { Pill } from './primitives/bits.jsx';
import { channel as findChannel, lang as findLang, STAGES } from '../../services/autoDub/mockData.js';

// Stages where the dub project is finished processing and the editor can open it.
// While 'dubbing' (or 'detected'/'failed') the project isn't ready — the editor
// would error and bounce back, so the card stays non-clickable.
const EDITABLE_STAGES = new Set(['review', 'publishing', 'published']);

/* One row in the live monitoring queue. The card opens the dub editor only once
   the dub is ready; the action cluster stops propagation so its buttons don't
   trigger that navigation. */
export default function QueueItem({ it, onApprove, onRetry, onReconnect, onOpenEditor }) {
	const st = STAGES[it.stage];
	const l = findLang(it.lang);
	const d = findChannel(it.dest);
	const canEdit = !!it.projectId && EDITABLE_STAGES.has(it.stage);
	const openEditor = canEdit ? () => onOpenEditor?.(it.projectId) : undefined;

	return (
		<div
			onClick={openEditor}
			className={`bg-white rounded-3xl border border-slate-200 shadow-small p-3.5 flex gap-3.5 items-center transition ${canEdit ? 'cursor-pointer hover:border-primary/40 hover:shadow-md' : ''}`}
		>
			<div className="w-[110px] flex-none"><Thumb grad={it.grad} dur={it.dur} radius={10} /></div>
			<div className="flex-1 min-w-0">
				<div className="text-sm font-semibold text-slate-800 truncate">{it.title}</div>
				<div className="flex gap-2 items-center mt-1.5 flex-wrap">
					<Pill>{l.name}</Pill>
					{it.mode === 'audio_track'
						? <Chip size="sm" variant="flat" color="secondary" startContent={<span className="ml-1"><FaLayerGroup size={11} /></span>}>Audio track</Chip>
						: d && <Pill className="pl-1"><ChannelAvatar ch={d} size={18} radius={5} />{d.name}</Pill>}
					<span className="text-[12px] text-slate-400">· detected {it.detected}</span>
					{it.cost > 0 && <span className="text-[12px] text-slate-400 flex items-center gap-1"><FaCoins size={11} />{it.cost} cr</span>}
				</div>
				{it.stage === 'dubbing' && (
					<div className="mt-2.5 flex items-center gap-2.5">
						<div className="flex-1 max-w-[320px] h-1.5 rounded-full bg-slate-200 overflow-hidden">
							<div className="h-full rounded-full bg-primary transition-[width] duration-500" style={{ width: it.progress + '%' }} />
						</div>
						<span className="text-[12px] font-bold text-primary">{Math.round(it.progress)}%</span>
					</div>
				)}
				{it.stage === 'failed' && it.error && (
					<div className="mt-2 text-[12.5px] text-rose-500 flex items-center gap-1.5"><FaTriangleExclamation size={13} />{it.error}</div>
				)}
			</div>
			<div className="flex-none flex flex-col gap-2 items-end" onClick={e => e.stopPropagation()}>
				<Chip size="sm" variant="flat" color={st.tone} className="font-semibold">{st.label}</Chip>
				{it.stage === 'review' && (
					<div className="flex gap-1.5">
						<Button size="sm" radius="full" variant="bordered" startContent={<FaPenToSquare size={13} />} isDisabled={!canEdit} onPress={openEditor}>Edit</Button>
						<Button size="sm" radius="full" color="primary" startContent={<FaCheck size={13} />} onPress={() => onApprove(it.id)}>Approve</Button>
					</div>
				)}
				{it.stage === 'failed' && (
					isReauthFailure(it.error)
						? <Button size="sm" radius="full" color="warning" className="font-semibold text-white" startContent={<FaLink size={13} />} onPress={() => onReconnect?.(it.id)}>Reconnect channel</Button>
						: <Button size="sm" radius="full" variant="bordered" startContent={<FaRotate size={13} />} onPress={() => onRetry(it.id)}>Retry</Button>
				)}
				{it.stage === 'published' && (
					<div className="flex items-center gap-2">
						<span className="text-[11.5px] text-slate-400">{it.publishedAt}</span>
						{it.publishedVideoId && (
							<Button
								size="sm"
								radius="full"
								variant="bordered"
								startContent={<FaArrowUpRightFromSquare size={12} />}
								onPress={() => window.open(`https://www.youtube.com/watch?v=${it.publishedVideoId}`, '_blank', 'noopener')}
							>
								Watch
							</Button>
						)}
					</div>
				)}
				{it.stage === 'publishing' && <span className="text-[11.5px] text-slate-400">Uploading…</span>}
			</div>
		</div>
	);
}
